import { AppointmentService } from '@agent/core/contact/appointment.service';
import { CsrFilter } from '@agent/core/contact/transfer/form/transfer-form.component';
import { ISearchService } from '@agent/core/search/search.service';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { NG1_SEARCH } from '@commons/core/core.provider.ajs';
import { BehaviorSubject, combineLatest, map, Observable, Subject } from 'rxjs';

import {
  AppointmentTargetDto,
  AppointmentTargetType,
  Group,
  intersect,
  InviteRequestDto,
  isArray,
  Logger,
  ProfileFacade,
  ProfileService,
  Skill,
  Team,
  UntilDestroy,
  untilDestroyed,
} from '@contact/upgrade';

@UntilDestroy()
@Component({
  selector: 'cng-appointment-invitation-dialog',
  templateUrl: 'appointment-invitation-dialog.component.html',
  styleUrls: ['./appointment-invitation-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentInvitationDialogComponent implements OnInit, OnDestroy {
  public availableSkills$: Observable<Skill[]>;
  public originalSelectedSkillUuids: string[] | undefined;
  public availableTeams$: Observable<Team[]>;
  public originalSelectedTeamUuid: string | undefined;
  public availableGroups$: Observable<Group[]>;
  public originalSelectedGroupUuid: string | undefined;
  public availableCsr$: Subject<any[]> = new Subject();
  public originalSelectedCsrUuid: string[] | undefined;

  private readonly teamUuidFilter$: Subject<string[]> = new BehaviorSubject([]);
  private readonly groupUuidFilter$: Subject<string[]> = new BehaviorSubject([]);
  private currentAgentId: string;

  // result objects
  private appointmentUuid: string;
  private selectedSkills: Skill[] | undefined;
  private selectedTeam: Team | undefined;
  private selectedGroup: Group | undefined;
  private selectedCsr: any[] | undefined;
  public diagResult: InviteRequestDto;

  constructor(
    private readonly logger: Logger,
    private readonly profileFacade: ProfileFacade,
    private readonly dialogRef: MatDialogRef<AppointmentInvitationDialogComponent>,
    private readonly appointmentService: AppointmentService,
    private readonly profileService: ProfileService,
    private readonly sessionDeprecatedService: SessionDeprecatedService,
    @Inject(MAT_DIALOG_DATA) private readonly data: any,
    @Inject(NG1_SEARCH) private readonly search: ISearchService
  ) {
    this.logger = this.logger.forSource('AppointmentInvitationDialogComponent');
  }

  ngOnInit(): void {
    this.appointmentUuid = !!this.data ? this.data.appointmentId : undefined;
    if (!!this.appointmentUuid) {
      this.appointmentService
        .getAppointment(this.appointmentUuid)
        .pipe(untilDestroyed(this))
        .subscribe(appointment => {
          this.originalSelectedSkillUuids = appointment.participantSkillList.map(skill => skill.uuid);
          this.originalSelectedCsrUuid = appointment.awaitingParticipants.map(csr => csr.uuid);
          const originalSelectedTeam = appointment.participantTargets.find(target => target.type === AppointmentTargetType.TEAM);
          const originalSelectedGroup = appointment.participantTargets.find(target => target.type === AppointmentTargetType.GROUP);
          this.originalSelectedTeamUuid = originalSelectedTeam && originalSelectedTeam.uuid;
          this.originalSelectedGroupUuid = originalSelectedGroup && originalSelectedGroup.uuid;
        });
      // skills preparation
      this.availableSkills$ = this.appointmentService.getVisioSkills();
      // groups preparation
      this.availableGroups$ = combineLatest([this.profileService.getDomainGroups(), this.groupUuidFilter$]).pipe(
        map(([groups, ids]) => (ids.length > 0 && intersect(groups, ids, ({ id }) => id)) || groups)
      );
      // teams preparation
      this.availableTeams$ = combineLatest([this.profileService.getDomainTeams(), this.teamUuidFilter$]).pipe(
        map(([teams, ids]) => (ids.length > 0 && intersect(teams, ids, ({ id }) => id)) || teams)
      );
    } else {
      this.logger.error('Appointment ID should not be undefined');
    }
    this.profileFacade.id$.pipe(untilDestroyed(this)).subscribe(id => (this.currentAgentId = id));
  }

  ngOnDestroy(): void {
    this.appointmentUuid = '';
    this.availableCsr$.complete();
    this.teamUuidFilter$.complete();
    this.groupUuidFilter$.complete();
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    const targets: AppointmentTargetDto[] = [];
    if (!!this.selectedGroup && this.selectedGroup.id) {
      targets.push({
        uuid: this.selectedGroup.id,
        name: this.selectedGroup.name,
        type: AppointmentTargetType.GROUP,
      });
    }
    if (!!this.selectedTeam && !!this.selectedTeam.id) {
      targets.push({
        uuid: this.selectedTeam.id,
        name: this.selectedTeam.name,
        type: AppointmentTargetType.TEAM,
      });
    }
    this.diagResult = {
      appointmentUuid: this.appointmentUuid,
      participantSkillList: !!this.selectedSkills
        ? this.selectedSkills.map((skill: Skill) => ({
            skillUuid: skill.id,
            name: skill.name,
          }))
        : [],
      awaitingParticipants: !!this.selectedCsr
        ? this.selectedCsr
            .filter(csr => !!csr.uuid || !!csr.id)
            .map((csr: any) => ({
              uuid: csr.uuid || csr.id,
              login: csr.login || '',
              firstName: csr.name || '',
              lastName: csr.lastName || '',
            }))
        : [],
      participantTargets: targets,
    };

    this.dialogRef.close(this.diagResult);
  }

  isFormValid(): boolean {
    return !!this.appointmentUuid && !!this.selectedSkills && this.selectedSkills.length > 0;
  }

  updateCandidateCsr(payload: CsrFilter) {
    const csr = (payload.csrPrefix && payload.csrPrefix.toLowerCase()) || '';
    const skills = payload.skillIds || [];
    if (!!skills && skills.length > 0) {
      this.search.searchCsr(payload.teamId || '', payload.groupId || '', skills, csr).then((csrList: any) => {
        const candidateCsrs = csrList
          .map(c => ({
            name: `${c.lastName} ${c.firstName}`,
            email: c.mail,
            uuid: c.uuid,
            team: c.team,
            groups: c.groups,
          }))
          .filter(c => c.uuid !== this.currentAgentId);
        this.availableCsr$.next(
          candidateCsrs.sort((a, b) => {
            const nameA = a.name.toUpperCase();
            const nameB = b.name.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          })
        );
        if (payload.teamId === null && payload.groupId === null) {
          this.filterGroupsAndTeamsBySkills(candidateCsrs);
        }
      });
    } else {
      this.availableCsr$.next([]);
    }
  }

  onTargetSelected(
    target: { skills: Skill[] | undefined; team: Team | undefined; group: Group | undefined; csr: any | any[] | undefined } | undefined
  ) {
    if (!!target) {
      this.selectedSkills = target.skills;
      this.selectedTeam = target.team;
      this.selectedGroup = target.group;
      if (!!target.csr && !isArray(target.csr)) {
        this.selectedCsr = [target.csr];
      } else {
        this.selectedCsr = target.csr;
      }
    }
  }

  private filterGroupsAndTeamsBySkills(candidateCsrs: any[]) {
    const candidateTeamUuids: string[] = [];
    const candidateGroupUuids: string[] = [];
    for (const _csr of candidateCsrs) {
      if (!!_csr.team && !!_csr.team.uuid && candidateTeamUuids.indexOf(_csr.team.uuid) < 0) {
        candidateTeamUuids.push(_csr.team.uuid);
      }
      if (!!_csr.groups && _csr.groups.length > 0) {
        for (const g of _csr.groups) {
          if (!!g.uuid && candidateGroupUuids.indexOf(g.uuid) < 0) {
            candidateGroupUuids.push(g.uuid);
          }
        }
      }
    }
    this.teamUuidFilter$.next(candidateTeamUuids);
    this.groupUuidFilter$.next(candidateGroupUuids);
  }
}
